<template>
  <Teleport to="body">
    <div class="fixed inset-0 z-40 flex justify-end" @click.self="$emit('close')">
      <!-- Backdrop. Click-anywhere-outside-to-close matches the
           UserDrawer / RoleDrawer pattern so navigation feels
           consistent across drawers. -->
      <div class="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>

      <!-- Panel -->
      <div
        class="relative h-full w-full max-w-2xl bg-white dark:bg-slate-900 shadow-2xl border-l border-gray-200 dark:border-white/10 overflow-y-auto"
        @click.stop
      >
        <!-- Header. Severity-tinted banner so the operator immediately
             reads "this is critical" vs "this is informational". -->
        <div
          class="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-white/10 px-6 py-4"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <div class="flex items-center gap-2 mb-1">
                <span :class="severityBadgeClass" class="px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wide">
                  {{ anomaly.severity }}
                </span>
                <span class="text-xs text-gray-500">{{ formatTimestamp(anomaly.timestamp) }}</span>
              </div>
              <h2 class="text-xl font-bold text-gray-900 dark:text-white">
                Cost spike on <span class="font-mono">{{ anomaly.warehouse }}</span>
              </h2>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {{ anomaly.description }}
              </p>
            </div>
            <button
              @click="$emit('close')"
              class="p-2 -m-2 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-white/10 hover:text-gray-700 dark:hover:text-gray-200 shrink-0"
              title="Close"
            >
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          <!-- KPI strip — expected / actual / delta. Keeps the
               at-a-glance numbers visible at the top of the drawer
               while the user scrolls through detail. -->
          <div class="grid grid-cols-3 gap-3 mt-4">
            <div class="rounded-lg bg-gray-50 dark:bg-white/5 px-3 py-2">
              <div class="text-[10px] uppercase tracking-wider text-gray-500">Expected</div>
              <div class="text-lg font-bold text-gray-900 dark:text-white">{{ formatCurrency(anomaly.expectedCost) }}</div>
            </div>
            <div class="rounded-lg bg-gray-50 dark:bg-white/5 px-3 py-2">
              <div class="text-[10px] uppercase tracking-wider text-gray-500">Actual</div>
              <div class="text-lg font-bold text-red-600 dark:text-red-400">{{ formatCurrency(anomaly.cost) }}</div>
            </div>
            <div class="rounded-lg bg-gray-50 dark:bg-white/5 px-3 py-2">
              <div class="text-[10px] uppercase tracking-wider text-gray-500">Δ vs baseline</div>
              <div class="text-lg font-bold text-red-600 dark:text-red-400">+{{ formatCurrency(deltaCost) }}</div>
            </div>
          </div>
        </div>

        <!-- Body -->
        <div class="px-6 py-5 space-y-7">
          <!-- ────────────────────────────────────────────────────── -->
          <!-- Recommendations — first because it's the most actionable. -->
          <!-- ────────────────────────────────────────────────────── -->
          <section>
            <div class="flex items-center justify-between mb-3">
              <h3 class="text-xs font-semibold uppercase tracking-wider text-gray-500">Recommendations</h3>
              <span v-if="loadingRecs" class="text-xs text-gray-400">Analyzing…</span>
            </div>
            <p v-if="!loadingRecs && !recommendations.length" class="text-sm text-gray-500 italic">
              No specific recommendations — see the heaviest-queries section below for context, and consider
              setting a resource monitor on this warehouse if you don't already have one.
            </p>
            <div v-else class="space-y-2">
              <div
                v-for="rec in recommendations"
                :key="rec.id"
                :class="[recBorderClass(rec.severity), recBgClass(rec.severity)]"
                class="rounded-lg border-l-4 px-4 py-3"
              >
                <div class="flex items-start justify-between gap-3">
                  <div class="min-w-0">
                    <div class="flex items-center gap-2 mb-1">
                      <span :class="recBadgeClass(rec.severity)" class="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide">
                        {{ rec.severity }}
                      </span>
                      <h4 class="text-sm font-semibold text-gray-900 dark:text-white">{{ rec.title }}</h4>
                    </div>
                    <p class="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{{ rec.body }}</p>
                  </div>
                  <a
                    v-if="rec.action"
                    :href="rec.action.href"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="px-3 py-1 text-xs font-medium rounded border border-purple-300 dark:border-purple-500/30 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-500/10 shrink-0 whitespace-nowrap"
                  >
                    {{ rec.action.label }} →
                  </a>
                </div>
              </div>
            </div>
          </section>

          <!-- ────────────────────────────────────────────────────── -->
          <!-- Heaviest queries — what actually drove the spike. -->
          <!-- ────────────────────────────────────────────────────── -->
          <section>
            <div class="flex items-center justify-between mb-3">
              <h3 class="text-xs font-semibold uppercase tracking-wider text-gray-500">Heaviest queries on this day</h3>
              <span v-if="loadingQueries" class="text-xs text-gray-400">Loading…</span>
            </div>
            <p v-if="!loadingQueries && !heavyQueries.length" class="text-sm text-gray-500 italic">
              No query history rows for this (warehouse, day) — ACCOUNT_USAGE.QUERY_HISTORY may not have
              caught up yet (45-min lag), or the queries ran outside QUERY_HISTORY's retention window.
            </p>
            <div v-else-if="heavyQueries.length" class="border border-gray-200 dark:border-white/10 rounded-lg overflow-hidden">
              <table class="w-full text-xs">
                <thead class="bg-gray-50 dark:bg-white/5 text-gray-500 uppercase tracking-wide">
                  <tr>
                    <th class="text-left px-3 py-2">User</th>
                    <th class="text-left px-3 py-2">Type</th>
                    <th class="text-right px-3 py-2">Duration</th>
                    <th class="text-right px-3 py-2">Scanned</th>
                    <th class="text-left px-3 py-2">Status</th>
                    <th class="text-right px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="q in heavyQueries" :key="q.queryId">
                    <tr
                      class="border-t border-gray-100 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-white/5 cursor-pointer"
                      @click="toggleExpanded(q.queryId)"
                    >
                      <td class="px-3 py-2 font-mono text-gray-700 dark:text-gray-300 truncate max-w-[120px]">{{ q.userName || '—' }}</td>
                      <td class="px-3 py-2 text-gray-600 dark:text-gray-400">{{ q.queryType || '—' }}</td>
                      <td class="px-3 py-2 text-right font-medium text-gray-900 dark:text-gray-100">{{ formatSeconds(q.execSeconds) }}</td>
                      <td class="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{{ formatBytes(q.bytesScanned) }}</td>
                      <td class="px-3 py-2">
                        <span :class="statusBadgeClass(q.executionStatus)" class="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase">{{ q.executionStatus || '—' }}</span>
                      </td>
                      <td class="px-3 py-2 text-right">
                        <svg
                          class="w-3 h-3 inline text-gray-400 transition-transform"
                          :class="expandedQuery === q.queryId ? 'rotate-180' : ''"
                          fill="none" viewBox="0 0 24 24" stroke="currentColor"
                        >
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </td>
                    </tr>
                    <tr v-if="expandedQuery === q.queryId" class="bg-gray-50 dark:bg-white/5">
                      <td colspan="6" class="px-3 py-3">
                        <dl class="grid grid-cols-4 gap-y-1 gap-x-3 text-[11px] mb-2">
                          <dt class="text-gray-500">Query ID</dt>
                          <dd class="col-span-3 font-mono text-gray-700 dark:text-gray-300 break-all">{{ q.queryId }}</dd>
                          <dt class="text-gray-500">Role</dt>
                          <dd class="col-span-3 font-mono text-gray-700 dark:text-gray-300">{{ q.roleName || '—' }}</dd>
                          <dt class="text-gray-500">Warehouse size</dt>
                          <dd class="col-span-3 text-gray-700 dark:text-gray-300">{{ q.warehouseSize || '—' }}</dd>
                          <dt class="text-gray-500">Started</dt>
                          <dd class="col-span-3 text-gray-700 dark:text-gray-300">{{ formatTimestamp(q.startTime) }}</dd>
                          <dt class="text-gray-500">Rows produced</dt>
                          <dd class="col-span-3 font-mono text-gray-700 dark:text-gray-300">{{ q.rowsProduced.toLocaleString() }}</dd>
                          <dt v-if="q.cloudServicesCost > 0" class="text-gray-500">Cloud services</dt>
                          <dd v-if="q.cloudServicesCost > 0" class="col-span-3 text-gray-700 dark:text-gray-300">${{ q.cloudServicesCost.toFixed(2) }}</dd>
                        </dl>
                        <pre class="text-[11px] font-mono text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-all bg-white dark:bg-slate-900 border border-gray-200 dark:border-white/10 rounded p-2 max-h-40 overflow-y-auto">{{ q.queryPreview }}</pre>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </section>

          <!-- ────────────────────────────────────────────────────── -->
          <!-- Top users on this day. -->
          <!-- ────────────────────────────────────────────────────── -->
          <section class="pb-4">
            <div class="flex items-center justify-between mb-3">
              <h3 class="text-xs font-semibold uppercase tracking-wider text-gray-500">Top users on this day</h3>
              <span v-if="loadingUsers" class="text-xs text-gray-400">Loading…</span>
            </div>
            <p v-if="!loadingUsers && !topUsers.length" class="text-sm text-gray-500 italic">
              No user attribution available.
            </p>
            <div v-else-if="topUsers.length" class="border border-gray-200 dark:border-white/10 rounded-lg overflow-hidden">
              <table class="w-full text-xs">
                <thead class="bg-gray-50 dark:bg-white/5 text-gray-500 uppercase tracking-wide">
                  <tr>
                    <th class="text-left px-3 py-2">User</th>
                    <th class="text-right px-3 py-2">Queries</th>
                    <th class="text-right px-3 py-2">Compute time</th>
                    <th class="text-right px-3 py-2">Share</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="u in topUsers" :key="u.userName" class="border-t border-gray-100 dark:border-white/5">
                    <td class="px-3 py-2 font-mono text-gray-900 dark:text-gray-100">{{ u.userName }}</td>
                    <td class="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{{ u.queryCount.toLocaleString() }}</td>
                    <td class="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{{ formatSeconds(u.execSeconds) }}</td>
                    <td class="px-3 py-2 text-right">
                      <!-- Share bar — visual cue beats reading 4 percentages. -->
                      <div class="flex items-center gap-2 justify-end">
                        <div class="w-16 h-1.5 rounded-full bg-gray-200 dark:bg-white/10 overflow-hidden">
                          <div class="h-full bg-purple-500" :style="{ width: Math.min(100, u.execSharePct) + '%' }"></div>
                        </div>
                        <span class="font-mono text-gray-700 dark:text-gray-300 w-10 text-right">{{ u.execSharePct.toFixed(0) }}%</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <p v-if="errorMessage" class="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10 px-3 py-2 rounded-lg border border-red-200 dark:border-red-500/30">
            {{ errorMessage }}
          </p>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import {
  buildAnomalyRecommendations,
  type AnomalyRecommendation,
  type CostAnomaly,
  type RecommendationSeverity,
} from '~/scripts/anomalyRecommendations'
import type { AnomalyHeavyQuery, AnomalyTopUser } from '~/composables/useCostData'

const props = defineProps<{ anomaly: CostAnomaly }>()
defineEmits(['close'])

const { fetchAnomalyTopQueries, fetchAnomalyTopUsers } = useCostData()

const heavyQueries = ref<AnomalyHeavyQuery[]>([])
const topUsers = ref<AnomalyTopUser[]>([])
const recommendations = ref<AnomalyRecommendation[]>([])
const loadingQueries = ref(false)
const loadingUsers = ref(false)
const loadingRecs = ref(false)
const errorMessage = ref<string | null>(null)

// Which heaviest-query row is expanded (showing query text + extra
// metadata). Single-row expansion — clicking another row collapses
// the previous one.
const expandedQuery = ref<string | null>(null)
function toggleExpanded(id: string) {
  expandedQuery.value = expandedQuery.value === id ? null : id
}

const deltaCost = computed(() => Math.max(0, props.anomaly.cost - props.anomaly.expectedCost))

// Pre-computed once when the anomaly lands. The day fragment is the
// `YYYY-MM-DD` slice of the ISO timestamp; SQL pivots use TO_DATE on
// the same string so we don't have to worry about TZ conversion.
const dayKey = computed(() => (props.anomaly.timestamp || '').slice(0, 10))

async function load() {
  errorMessage.value = null
  loadingQueries.value = true
  loadingUsers.value = true
  loadingRecs.value = true

  // Fan out — either side can fail without blanking the other. The
  // recommendations engine waits for both to land so it can build
  // rules that reference users + queries, but the per-section
  // empty-states render their own messages independently.
  const qPromise = fetchAnomalyTopQueries(props.anomaly.warehouse, dayKey.value)
    .then((rows) => { heavyQueries.value = rows })
    .catch((err: any) => {
      console.error('[AnomalyDrawer] top-queries failed:', err)
      errorMessage.value = err?.message || 'Failed to load heaviest queries.'
    })
    .finally(() => { loadingQueries.value = false })

  const uPromise = fetchAnomalyTopUsers(props.anomaly.warehouse, dayKey.value)
    .then((rows) => { topUsers.value = rows })
    .catch((err: any) => {
      console.error('[AnomalyDrawer] top-users failed:', err)
      // Don't override an existing query error — the user already has a banner.
      if (!errorMessage.value) errorMessage.value = err?.message || 'Failed to load top users.'
    })
    .finally(() => { loadingUsers.value = false })

  await Promise.allSettled([qPromise, uPromise])
  recommendations.value = buildAnomalyRecommendations(
    props.anomaly,
    heavyQueries.value,
    topUsers.value,
  )
  loadingRecs.value = false
}

// Re-load if the parent swaps the anomaly while the drawer's open
// (currently nothing does, but cheap to guard).
watch(() => props.anomaly.id, load, { immediate: true })

// ---------------------------------------------------------------------------
// Style helpers
// ---------------------------------------------------------------------------

const severityBadgeClass = computed(() => {
  switch (props.anomaly.severity) {
    case 'critical': return 'bg-red-600 text-white'
    case 'warning':  return 'bg-yellow-600 text-white'
    default:         return 'bg-blue-600 text-white'
  }
})

function recBorderClass(s: RecommendationSeverity): string {
  return ({
    critical: 'border-red-500',
    warning: 'border-yellow-500',
    info: 'border-blue-500',
  })[s]
}
function recBgClass(s: RecommendationSeverity): string {
  return ({
    critical: 'bg-red-50 dark:bg-red-500/10',
    warning: 'bg-yellow-50 dark:bg-yellow-500/10',
    info: 'bg-blue-50 dark:bg-blue-500/10',
  })[s]
}
function recBadgeClass(s: RecommendationSeverity): string {
  return ({
    critical: 'bg-red-600 text-white',
    warning: 'bg-yellow-600 text-white',
    info: 'bg-blue-600 text-white',
  })[s]
}

function statusBadgeClass(status: string): string {
  if (status === 'SUCCESS') return 'bg-green-500/15 text-green-700 dark:text-green-300'
  if (status === 'FAIL' || status === 'FAILED_WITH_ERROR') return 'bg-red-500/15 text-red-700 dark:text-red-300'
  if (status === 'RUNNING' || status === 'QUEUED') return 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
  return 'bg-gray-500/15 text-gray-600 dark:text-gray-300'
}

// ---------------------------------------------------------------------------
// Formatting (kept local — Cost helpers don't have a seconds/bytes formatter)
// ---------------------------------------------------------------------------

const { formatCurrency } = useCostFormatting()

function formatSeconds(s: number): string {
  if (!Number.isFinite(s) || s <= 0) return '0s'
  if (s < 60) return `${s.toFixed(1)}s`
  const m = Math.floor(s / 60)
  const rem = Math.round(s - m * 60)
  if (m < 60) return `${m}m ${rem}s`
  const h = Math.floor(m / 60)
  return `${h}h ${m - h * 60}m`
}

function formatBytes(b: number): string {
  if (!Number.isFinite(b) || b <= 0) return '—'
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  let v = b; let i = 0
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++ }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`
}

const { formatInUserTz } = useUserTimezone()

function formatTimestamp(iso: string): string {
  if (!iso) return '—'
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return iso
  return formatInUserTz(new Date(t), { withSeconds: true })
}
</script>
